import React, { useState } from 'react';
import { useGameStore } from '@/store/gameStore';
import { LOCATIONS, NPCS } from '@/data/gameData';
import { LOCATION_DATA } from '@/data/locationDescriptions';
import { WORLD_LOCATIONS } from '@/data/worldSystem';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DialogueSystem } from './DialogueSystem';
import { QuestLog } from './QuestLog';
import { LocationExplorer } from './LocationExplorer';
import { PlayerStats } from './PlayerStats';
import { CombatScreen } from './CombatScreen';
import { WorldMap } from './WorldMap';
import { OpenWorldExplorer } from './OpenWorldExplorer';
import { Sword, Map, Compass } from 'lucide-react';

export const GameWorld: React.FC = () => {
  const { character, setScreen } = useGameStore();
  const [currentLocation, setCurrentLocation] = useState(LOCATIONS[0]);
  const [selectedNPC, setSelectedNPC] = useState<string | null>(null);
  const [activeDialogueNPC, setActiveDialogueNPC] = useState<string | null>(null);
  const [showCombat, setShowCombat] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [gameMode, setGameMode] = useState<'exploration' | 'dialogue' | 'location'>('exploration');
  const [encounterEnemy, setEncounterEnemy] = useState<string | null>(null);

  if (!character) {
    return null;
  }

  const locationNPCs = NPCS.filter((npc) => {
    if (npc.id === 'nadia') return currentLocation === 'Vestuvia';
    return true;
  });

  const locationInfo = LOCATION_DATA[currentLocation];

  return (
    <div className="w-full h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-black overflow-auto">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-orange-500">Welcome to Dark Hells</h1>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowMap(true)}
              className="bg-blue-900 hover:bg-blue-800 text-white flex items-center gap-2"
            >
              <Map className="w-4 h-4" />
              Map
            </Button>
            <Button
              onClick={() => setShowCombat(true)}
              className="bg-red-900 hover:bg-red-800 text-white flex items-center gap-2"
            >
              <Sword className="w-4 h-4" />
              Fight
            </Button>
            <Button
              onClick={() => setScreen('title')}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              Menu
            </Button>
          </div>
        </div>

        {/* Character Info */}
        <Card className="mb-8 p-6 bg-slate-800 border-orange-500">
          <h2 className="text-xl font-bold text-orange-500 mb-4">Character Info</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-white">
            <div>
              <p className="text-gray-400">Race</p>
              <p className="font-semibold capitalize">{character.race.replace('-', ' ')}</p>
            </div>
            <div>
              <p className="text-gray-400">Class</p>
              <p className="font-semibold capitalize">{character.class}</p>
            </div>
            <div>
              <p className="text-gray-400">Outfit</p>
              <p className="font-semibold capitalize">{character.outfit}</p>
            </div>
            <div>
              <p className="text-gray-400">Weapon</p>
              <p className="font-semibold">{character.weapon}</p>
            </div>
          </div>
        </Card>

        {/* Game Mode Toggle */}
        <div className="mb-6 flex gap-2">
          <Button
            onClick={() => setGameMode('exploration')}
            className={`flex items-center gap-2 ${
              gameMode === 'exploration'
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-slate-700 hover:bg-slate-600'
            }`}
          >
            <Compass className="w-4 h-4" />
            Open World
          </Button>
          <Button
            onClick={() => setGameMode('location')}
            className={`flex items-center gap-2 ${
              gameMode === 'location' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-700 hover:bg-slate-600'
            }`}
          >
            <MapPin className="w-4 h-4" />
            Locations
          </Button>
        </div>

        {/* Exploration Mode */}
        {gameMode === 'exploration' && (
          <OpenWorldExplorer
            currentLocation={currentLocation}
            onEncounter={(enemy) => {
              setEncounterEnemy(enemy);
              setShowCombat(true);
            }}
            onNavigate={(locationId) => {
              const locName = WORLD_LOCATIONS.find((l) => l.id === locationId)?.name;
              if (locName) {
                setCurrentLocation(locName);
                setSelectedNPC(null);
                setActiveDialogueNPC(null);
              }
            }}
          />
        )}

        {/* Location Mode */}
        {gameMode === 'location' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left Sidebar - Locations */}
            <div className="space-y-4 max-h-screen overflow-y-auto">
              <h3 className="text-xl font-bold text-orange-500 sticky top-0 bg-slate-900 py-2">Locations</h3>
              <div className="space-y-2">
                {LOCATIONS.map((location) => (
                  <Button
                    key={location}
                    onClick={() => {
                      setCurrentLocation(location);
                      setSelectedNPC(null);
                      setActiveDialogueNPC(null);
                    }}
                    variant={currentLocation === location ? 'default' : 'outline'}
                    className={`w-full justify-start text-sm ${
                      currentLocation === location
                        ? 'bg-orange-600 border-orange-600 text-white'
                        : 'border-gray-600 text-gray-300 hover:text-white'
                    }`}
                  >
                    {location}
                  </Button>
                ))}
              </div>
            </div>

            {/* Center - Location & NPCs */}
            <div className="lg:col-span-2 space-y-4">
              {/* Location Explorer */}
              <LocationExplorer location={locationInfo} />

            {/* NPCs Section */}
            <Card className="p-6 bg-slate-800 border-orange-500">
              <h3 className="text-2xl font-bold text-orange-500 mb-4">Inhabitants</h3>

              {locationNPCs.length > 0 ? (
                <div className="space-y-2">
                    {locationNPCs.map((npc) => (
                      <Button
                        key={npc.id}
                        onClick={() => {
                          setSelectedNPC(npc.id);
                          setActiveDialogueNPC(null);
                        }}
                        variant={selectedNPC === npc.id ? 'default' : 'outline'}
                        className={`w-full justify-start ${
                          selectedNPC === npc.id
                            ? 'bg-orange-600 border-orange-600 text-white'
                            : 'border-gray-600 text-gray-300 hover:text-white'
                        }`}
                      >
                        {npc.name} {npc.romanceable && '💕'}
                      </Button>
                    ))}
                </div>
              ) : (
                <p className="text-gray-400 italic">No one here to talk to.</p>
              )}
            </Card>

            {/* NPC Details */}
            {selectedNPC && !activeDialogueNPC && (
              <Card className="p-6 bg-slate-800 border-orange-500">
                {NPCS.map((npc) => {
                  if (npc.id !== selectedNPC) return null;
                  return (
                    <div key={npc.id}>
                      <h3 className="text-2xl font-bold text-orange-500 mb-2">{npc.name}</h3>
                      <p className="text-gray-400 mb-2">
                        {npc.race.replace('-', ' ')} - {npc.description}
                      </p>
                      {npc.romanceable && (
                        <p className="text-pink-400 mb-4">
                          💕 Romance Route: {npc.romance.replace('-', ' ')}
                        </p>
                      )}
                      <p className="text-gray-300 mb-6">
                        "Greetings, adventurer. I sense great potential in you. What brings you to this place?"
                      </p>
                      <div className="space-y-2">
                        <Button
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => setActiveDialogueNPC(npc.id)}
                        >
                          Engage in Conversation
                        </Button>
                        {npc.romanceable && (
                          <Button className="w-full bg-pink-600 hover:bg-pink-700 text-white">
                            Show Interest
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}

            {/* Dialogue System */}
            {activeDialogueNPC && (
              <DialogueSystem
                npcId={activeDialogueNPC}
                npcName={NPCS.find((n) => n.id === activeDialogueNPC)?.name || ''}
                onClose={() => setActiveDialogueNPC(null)}
              />
            )}
          </div>

          {/* Right Sidebar - Stats & Quest Log */}
          <div className="space-y-4 max-h-screen overflow-y-auto">
            {/* Player Stats */}
            <div className="bg-slate-800 border border-orange-500 rounded-lg p-4">
              <h3 className="text-lg font-bold text-orange-500 mb-4">Character Stats</h3>
              <PlayerStats character={character} />
            </div>

              {/* Quest Log */}
              <div>
                <h3 className="text-lg font-bold text-orange-500 mb-3">Quest Log</h3>
                <QuestLog />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Combat Screen - Always Overlay */}
      {showCombat && <CombatScreen onClose={() => setShowCombat(false)} />}

      {/* World Map */}
      {showMap && (
        <WorldMap
          currentLocation={currentLocation}
          onLocationSelect={(loc) => {
            setCurrentLocation(loc);
            setShowMap(false);
          }}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>
  );
};